/**
 * Register Page
 * User registration form
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { geocodeCityState } from '../utils/geocoding';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    display_name: '',
    city: '',
    state: '',
    account_role: ''
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [locationValid, setLocationValid] = useState(null); // null = not checked, true = valid, false = invalid
  const [locationChecking, setLocationChecking] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Reset location validation when city or state changes
    if (e.target.name === 'city' || e.target.name === 'state') {
      setLocationValid(null);
    }
  };

  const validateLocation = async () => {
    if (!formData.city || !formData.state) {
      setLocationValid(false);
      return;
    }
    
    setLocationChecking(true);
    try {
      await geocodeCityState(formData.city, formData.state);
      setLocationValid(true);
    } catch (err) {
      setLocationValid(false);
    } finally {
      setLocationChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Check location is valid before submitting
    if (locationValid !== true) {
      setError('Please enter a valid city and state');
      return;
    }
    
    setLoading(true);

    try {
      const response = await apiClient.post('/auth/register', formData);
      
      if (response.data.success) {
        // Login user with returned token and user data
        login(response.data.user, response.data.token);

// Show popup reminder
  alert('Registration successful! Go to My Account → Edit Profile to finish setting up your account.');
        
// Redirect to home
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '500px', margin: '50px auto', padding: '20px' }}>
      <h1>Register for Subterra</h1>
      
      {error && (
        <div style={{ 
          backgroundColor: '#fee', 
          color: '#c00', 
          padding: '10px', 
          marginBottom: '20px',
          borderRadius: '4px'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '15px' }}>
          <label>Email:</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Password:</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="8"
              style={{ width: '100%', padding: '8px', marginTop: '5px', paddingRight: '40px' }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '4px 8px',
                color: '#666'
              }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>
          <small>At least 8 characters</small>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Username:</label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
          <small>Letters, numbers, and underscores only</small>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Display Name:</label>
          <input
            type="text"
            name="display_name"
            value={formData.display_name}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>City:</label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            onBlur={validateLocation}
            required
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>State:</label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              onBlur={validateLocation}
              required
              maxLength="2"
              placeholder="CT"
              style={{ 
                width: '100%', 
                padding: '8px', 
                marginTop: '5px',
                paddingRight: '40px',
                borderColor: locationValid === false ? '#c00' : locationValid === true ? '#0c0' : undefined
              }}
            />
            {locationChecking && (
              <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)' }}>
                ⏳
              </span>
            )}
            {!locationChecking && locationValid === true && (
              <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#0c0', fontSize: '18px' }}>
                ✓
              </span>
            )}
            {!locationChecking && locationValid === false && (
              <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#c00', fontSize: '18px' }}>
                ✗
              </span>
            )}
          </div>
          <small style={{ color: locationValid === false ? '#c00' : undefined }}>
            {locationValid === false ? 'Invalid location - please check city and state' : '2-letter state code (e.g., CT, NY)'}
          </small>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Account Type:</label>
          <select
            name="account_role"
            value={formData.account_role}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          >
            <option value="">Select account type...</option>
	    <option value="band">Band/Artist</option>
            <option value="venue">Venue</option>
            <option value="promoter">Promoter</option>
            <option value="photographer">Photographer</option>
            <option value="vendor">Vendor</option>
            <option value="studio">Studio</option>
            <option value="fan">Fan</option>
          </select>
        </div>

        <button 
          type="submit" 
          disabled={loading}
          style={{ 
            width: '100%', 
            padding: '10px', 
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '16px'
          }}
        >
          {loading ? 'Registering...' : 'Register'}
        </button>
      </form>

      <p style={{ marginTop: '20px', textAlign: 'center' }}>
        Already have an account? <Link to="/login">Login here</Link>
      </p>
    </div>
  );
};

export default RegisterPage;
